import { useState } from 'react';
import {
  Alert,
  Box,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import { useTranslation } from 'react-i18next';
import { LOAD_COLORS } from '../../domain/places/presentation';

const toastSx = {
  position: 'absolute',
  zIndex: 7,
  right: '50%',
  bottom: { xs: 'max(18px, env(safe-area-inset-bottom))', sm: 24 },
  width: { xs: 'calc(100vw - 24px)', sm: 'auto' },
  px: 2,
  py: 1.25,
  borderRadius: '12px',
  transform: 'translateX(50%)',
  boxShadow: '0 8px 25px rgba(17,39,35,.15)',
  fontSize: 13,
  fontWeight: 700,
};

const mapItems = [
  { title: 'loadLevelLegend.calm', color: LOAD_COLORS.green },
  { title: 'loadLevelLegend.medium', color: LOAD_COLORS.orange },
  { title: 'loadLevelLegend.busy', color: LOAD_COLORS.red },
  { title: 'loadLevelLegend.veryBusy', color: LOAD_COLORS.claret },
  { title: 'loadLevelLegend.closed', color: LOAD_COLORS.gray },
  { title: 'loadLevelLegend.unknown', color: LOAD_COLORS.blue },
];

export default function MapActions({ onLocate, locationStatus, error }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 599.95px), (max-height: 499.95px)');
  const [legendOpen, setLegendOpen] = useState(false);

  return (
    <>
      <IconButton
        sx={{
          position: 'absolute',
          zIndex: 4,
          insetInlineEnd: { xs: 12, sm: 20 },
          bottom: { xs: 'max(76px, calc(env(safe-area-inset-bottom) + 66px))', sm: 100 },
          width: { xs: 48, sm: 45 },
          height: { xs: 48, sm: 45 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: { xs: 3, sm: 1 },
          color: 'primary.main',
          bgcolor: 'background.paper',
          boxShadow: '0 10px 30px rgba(17,39,35,.14)',
        }}
        onClick={onLocate}
        title={t('location.back')}
        aria-label={t('location.back')}
      >
        <MyLocationRoundedIcon />
      </IconButton>

      {isMobile
        ? <>
          <IconButton
            onClick={() => setLegendOpen((open) => !open)}
            aria-label={t('loadLevelLegend.unknown')}
            sx={{
              position: 'absolute',
              zIndex: 4,
              insetInlineEnd: 12,
              bottom: 'max(18px, env(safe-area-inset-bottom))',
              width: 48,
              height: 48,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              color: 'primary.main',
              bgcolor: 'background.paper',
              boxShadow: '0 10px 30px rgba(17,39,35,.14)',
            }}
          >
            {legendOpen ? <CloseRoundedIcon /> : <MapRoundedIcon />}
          </IconButton>

          <Collapse in={legendOpen} orientation="horizontal" timeout={180}>
            <Paper
              elevation={6}
              sx={{
                position: 'absolute',
                zIndex: 4,
                insetInlineEnd: 68,
                bottom: 'max(18px, env(safe-area-inset-bottom))',
                maxWidth: 'calc(100vw - 80px)',
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" sx={{ gap: 1.1, overflowX: 'auto' }}>
                {mapItems.map((item) => (
                  <Stack key={item.title} sx={{ minWidth: 42, alignItems: 'center', gap: 0.4 }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: 1, bgcolor: item.color }} />
                    <Typography sx={{ fontSize: 10.5, color: 'text.primary', whiteSpace: 'nowrap' }}>
                      {t(item.title)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Collapse>
        </>
        : <Stack
          sx={{
            position: 'absolute',
            zIndex: 4,
            insetInlineEnd: 20,
            bottom: { md: '25%', xl: '30%', xxl: '35%' },
            p: 1,
            py: 2,
            display: 'grid',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'primary.main',
            bgcolor: 'background.paper',
            boxShadow: '0 10px 30px rgba(17,39,35,.14)',
            gap: 1,
          }}
        >
          {mapItems.map((item, index) => (
            <Stack key={item.title} sx={{ alignItems: 'center', gap: 0.3 }}>
              <Box sx={{ width: 25, height: 25, borderRadius: 0.5, bgcolor: item.color }} />
              <Typography sx={{ fontSize: 14, color: 'text.primary', textAlign: 'center' }}>
                {t(item.title)}
              </Typography>
              {index < mapItems.length - 1 && <Divider flexItem />}
            </Stack>
          ))}
        </Stack>
      }

      {locationStatus === 'fallback' && (
        <Alert severity="warning" sx={toastSx}>{t('location.fallback')}</Alert>
      )}
      {error && <Alert severity="error" sx={toastSx}>{error}</Alert>}
    </>
  );
}
